// src/utils/richText.js
// Gemeinsame Helfer für Rich-Text-Felder (Tiptap-HTML), die NICHT nur im
// Research-Tool angezeigt werden (seit US-PEND-010 / ADR 0058 auch die
// Pendenz-Beschreibung). Gespiegelt zu SANITIZE_CONFIG in
// azureResearchService.js, aber bewusst eigenständig (kein Cross-Import
// zwischen Feature-Services).
import DOMPurify from "dompurify";

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "span", "strong", "b", "em", "i", "u", "s", "strike",
    "sub", "sup", "ul", "ol", "li", "a",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "style", "class"],
};

// Vor dem Speichern UND vor jedem dangerouslySetInnerHTML anwenden (XSS-Schutz).
export const sanitizeRichText = (html) => DOMPurify.sanitize(html || "", SANITIZE_CONFIG);

// Reiner Text ohne Formatierung – für Suche/Filter, Excel-Export und
// Sitzungsprotokoll (dort sind nur Zellenwerte sinnvoll, keine Tags).
// Blockelement-Enden werden vor dem Strippen zu Zeilenumbrüchen, damit z.B.
// Aufzählungspunkte nicht zusammenlaufen.
export const richTextToPlainText = (html) => {
  if (!html) return "";
  const withBreaks = html
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n");
  const container = document.createElement("div");
  container.innerHTML = sanitizeRichText(withBreaks);
  const text = container.textContent || "";
  return text.replace(/\n{3,}/g, "\n\n").replace(/[ \t]+\n/g, "\n").trim();
};

// Klartext MIT Listenstruktur – für das PDF-Sitzungsprotokoll. Anders als
// richTextToPlainText bleiben Aufzählungen erhalten: jeder <li> wird zu einer
// Zeile mit Marker ("• " bzw. "1. "), Verschachtelung als 2 Leerzeichen pro
// Ebene vorangestellt. Tiptap verpackt den Inhalt eines <li> in ein <p> –
// das darf KEINE zusätzliche Leerzeile erzeugen (sonst stehen die Punkte
// auseinandergerissen). Weitere Absätze innerhalb eines <li> werden auf die
// Textspalte des Punkts eingerückt. Das Format liest generateSitzungPdf.js
// (LIST_ITEM_RE) für den hängenden Einzug wieder ein.
// Fett/kursiv/unterstrichen werden als Steuerzeichen (RICH_STYLE_MARKS,
// Unicode Private Use Area) um jeden Textabschnitt gelegt – pro Abschnitt
// geschlossen, damit jede Zeile für sich gültig bleibt. Das Ergebnis ist nur
// für das PDF gedacht (generateSitzungPdf.js parst die Marken), nicht für
// Anzeige/Suche/Excel.
export const RICH_STYLE_MARKS = {
  bold: ["", ""],
  italic: ["", ""],
  underline: ["", ""],
};
const STYLE_TAGS = {
  strong: "bold",
  b: "bold",
  em: "italic",
  i: "italic",
  u: "underline",
};

export const richTextToListText = (html) => {
  if (!html) return "";
  const container = document.createElement("div");
  container.innerHTML = sanitizeRichText(html);

  const lines = [];
  let cur = "";
  let open = false;
  const endLine = () => {
    if (open) lines.push(cur);
    cur = "";
    open = false;
  };
  const startLine = (prefix) => {
    endLine();
    cur = prefix;
    open = true;
  };
  const appendText = (text, indent, styles) => {
    if (!text) return;
    if (!open) startLine(indent);
    const active = Object.keys(RICH_STYLE_MARKS).filter((k) => styles[k]);
    cur +=
      active.map((k) => RICH_STYLE_MARKS[k][0]).join("") +
      text +
      active.map((k) => RICH_STYLE_MARKS[k][1]).join("");
  };

  const walk = (node, depth, styles = {}) => {
    const indent = "  ".repeat(depth);
    for (const child of node.childNodes) {
      if (child.nodeType === 3) {
        // Altdaten (reiner Text) enthalten echte Zeilenumbrüche.
        const parts = child.textContent.split("\n");
        parts.forEach((part, i) => {
          if (i > 0) {
            if (!open) startLine(indent);
            endLine();
          }
          appendText(part, indent, styles);
        });
        continue;
      }
      if (child.nodeType !== 1) continue;
      const tag = child.nodeName.toLowerCase();
      if (tag === "br") {
        if (!open) startLine(indent);
        endLine();
      } else if (tag === "ul" || tag === "ol") {
        endLine();
        let n = parseInt(child.getAttribute("start"), 10) || 1;
        for (const li of child.children) {
          if (li.nodeName.toLowerCase() !== "li") continue;
          startLine(`${indent}${tag === "ol" ? `${n++}.` : "•"} `);
          walk(li, depth + 1, styles);
          endLine();
        }
      } else if (tag === "p" || tag === "div") {
        const wasOpen = open;
        walk(child, depth, styles);
        // Leerer Absatz ausserhalb eines Listenpunkts = bewusste Leerzeile.
        if (!open && !wasOpen) lines.push("");
        endLine();
      } else if (STYLE_TAGS[tag]) {
        walk(child, depth, { ...styles, [STYLE_TAGS[tag]]: true });
      } else {
        walk(child, depth, styles);
      }
    }
  };
  walk(container, 0);
  endLine();

  return lines
    .map((l) => l.replace(/\s+$/, ""))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+|\n+$/g, "");
};

// Altdaten (US-PEND-001) waren reiner Text ohne Tags – für sie liefert
// isRichTextEmpty/richTextToPlainText dieselben Ergebnisse wie vorher, echte
// XSS-Sanitisierung + Rendering funktionieren mit Klartext identisch (keine
// Tags = nichts zu sanitisieren).
export const isRichTextEmpty = (html) => richTextToPlainText(html).length === 0;
